import React, { useState, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { X, CreditCard } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { addCreditCard, updateCreditCard } from '../../store/thunk/userThunk';
import { selectAuthLoading } from '../../store/reducer/authSlice';
import toast from 'react-hot-toast';
import ButtonLoader from '../../shared/loader/ButtonLoader';
import { fetchUserPayments } from '../../store/thunk/authThunk';

const AddCardDetails = ({ user, open, setOpen, title, btnname, cardData, onSuccess }) => {
    const dispatch = useDispatch();
    const [errors, setErrors] = useState({});
    const loading = useSelector(selectAuthLoading);
    const [cardDetails, setCardDetails] = useState({
        cardNumber: '',
        name: '',
        expiryDate: '',
        cvv: '',
        sameAsFeaturePuchases: false,
        cardType: '',
    });

    // Populate form when editing existing card
    useEffect(() => {
        if (cardData && open) {
            // console.log('Populating form with card data:', cardData);
            // setCardDetails({
            //     cardNumber: cardData.card_number || '',
            //     name: cardData.card_holder_name || '',
            //     expiryDate: cardData.card_expiry_date || '',
            //     cvv: '',
            //     sameAsFeaturePuchases: cardData.save_card === 1 || false,
            //     cardType: cardData.card_type || '',
            // });
        } else if (!cardData && open) {
            // Reset form for new card
            resetForm();
        }
    }, [cardData, open]);

    const detectCardType = (number) => {
        const cleanNumber = number.replace(/\s/g, '');

        if (/^4/.test(cleanNumber)) return 'visa';
        if (/^5[1-5]/.test(cleanNumber)) return 'mastercard';
        if (/^3[47]/.test(cleanNumber)) return 'amex';
        if (/^6(?:011|5)/.test(cleanNumber)) return 'discover';

        return '';
    };

    // Format CVV (only numbers)
    const formatCVV = (value) => {
        const cleanValue = value.replace(/\D/g, '');
        return cleanValue.substring(0, 4);
    };

    // Format card number with spaces
    const formatCardNumber = (value) => {
        const cleanValue = value.replace(/\s/g, '');
        return cleanValue.replace(/(\d{4})(?=\d)/g, '$1 ');
    };

    // Format expiry date MM/YY - allows proper deletion
    const formatExpiryDate = (value) => {
        // Remove all non-digit characters including the slash
        const cleanValue = value.replace(/\D/g, '');

        // Return empty if no digits
        if (cleanValue.length === 0) {
            return '';
        }

        // Only add slash if we have at least 2 digits for month
        if (cleanValue.length >= 3) {
            return cleanValue.substring(0, 2) + '/' + cleanValue.substring(2, 4);
        }

        // Return just the digits if less than 3
        return cleanValue.substring(0, 2);
    };

    // Validate card number using Luhn algorithm
    const validateCardNumber = (number) => {
        const cleanNumber = number.replace(/\s/g, '');

        if (cleanNumber.length < 13 || cleanNumber.length > 19) return false;

        let sum = 0;
        let isEven = false;

        for (let i = cleanNumber.length - 1; i >= 0; i--) {
            let digit = parseInt(cleanNumber[i]);

            if (isEven) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }

            sum += digit;
            isEven = !isEven;
        }

        return sum % 10 === 0;
    };

    // Validate expiry date
    const validateExpiryDate = (value) => {
        if (!value.includes('/')) return false;

        const [month, year] = value.split('/');
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear() % 100;
        const currentMonth = currentDate.getMonth() + 1;

        const expMonth = parseInt(month);
        const expYear = parseInt(year);

        if (expMonth < 1 || expMonth > 12) return false;
        if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth))
            return false;

        return true;
    };

    // Reset form
    const resetForm = () => {
        setCardDetails({
            cardNumber: '',
            name: '',
            expiryDate: '',
            cvv: '',
            sameAsFeaturePuchases: false,
            cardType: '',
        });
        setErrors({});
    };

    // Close modal with reset
    const handleClose = () => {
        if (!loading) {
            resetForm();
            setOpen(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let formattedValue = value;
        let newErrors = { ...errors };

        if (type === 'checkbox') {
            setCardDetails({ ...cardDetails, [name]: checked });
            return;
        }

        // Apply formatting based on field
        switch (name) {
            case 'cardNumber':
                formattedValue = formatCardNumber(value);
                const detectedType = detectCardType(value);

                // Limit length (19 chars including spaces)
                if (formattedValue.length > 19) {
                    formattedValue = formattedValue.substring(0, 19);
                }

                // Validate card number
                if (formattedValue.length > 0 && !validateCardNumber(formattedValue)) {
                    newErrors.cardNumber = 'Invalid card number';
                } else {
                    delete newErrors.cardNumber;
                }
                // Update form with both cardNumber and cardType
                setCardDetails({
                    ...cardDetails,
                    [name]: formattedValue,
                    cardType: detectedType,
                });
                setErrors(newErrors);
                return;

            case 'expiryDate':
                formattedValue = formatExpiryDate(value);

                // Validate expiry date
                if (formattedValue.length === 5 && !validateExpiryDate(formattedValue)) {
                    newErrors.expiryDate = 'Invalid or expired date';
                } else {
                    delete newErrors.expiryDate;
                }
                break;

            case 'cvv':
                formattedValue = formatCVV(value);

                // Validate CVV length
                if (formattedValue.length > 0 && formattedValue.length < 3) {
                    newErrors.cvv = 'CVV must be at least 3 digits';
                } else {
                    delete newErrors.cvv;
                }
                break;

            case 'name':
                // Validate name
                if (value.length < 2) {
                    newErrors.name = 'Name must be at least 2 characters';
                } else {
                    delete newErrors.name;
                }
                break;

            default:
                break;
        }

        setCardDetails({ ...cardDetails, [name]: formattedValue });
        setErrors(newErrors);
    };

    // Validate form before submission
    const validateForm = () => {
        const newErrors = {};

        if (!cardDetails.cardNumber) {
            newErrors.cardNumber = 'Card number is required';
        } else if (!validateCardNumber(cardDetails.cardNumber)) {
            newErrors.cardNumber = 'Invalid card number';
        }

        if (!cardDetails.name) {
            newErrors.name = 'Cardholder name is required';
        } else if (cardDetails.name.length < 2) {
            newErrors.name = 'Name must be at least 2 characters';
        }

        if (!cardDetails.expiryDate) {
            newErrors.expiryDate = 'Expiry date is required';
        } else if (!validateExpiryDate(cardDetails.expiryDate)) {
            newErrors.expiryDate = 'Invalid or expired date';
        }

        if (!cardDetails.cvv) {
            newErrors.cvv = 'CVV is required';
        } else if (cardDetails.cvv.length < 3) {
            newErrors.cvv = 'CVV must be at least 3 digits';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        // Validate form first
        if (!validateForm()) {
            toast.error('Please fix the errors before submitting');
            return;
        }

        if (!cardDetails) {
            toast.error('Please fill in all card details');
            return;
        }

        // Determine if we're adding or updating
        const isEditing = cardData && cardData.payment_id;

        const payload = {
            user_id: user?.user_id,
            card_number: cardDetails?.cardNumber,
            card_holder_name: cardDetails?.name,
            card_expiry_date: cardDetails?.expiryDate,
            card_type: cardDetails?.cardType,
            cvv: cardDetails?.cvv,
            save_card: cardDetails?.sameAsFeaturePuchases ? 1 : 0,
            action: isEditing ? 'edit-credit-card' : 'add-credit-card',
        };

        // Add payment_id for update operations
        if (isEditing) {
            payload.payment_card_id = cardData.payment_id;
        }

        // Choose the appropriate thunk based on operation
        const cardPromise = dispatch(
            isEditing ? updateCreditCard(payload) : addCreditCard(payload),
        ).unwrap();

        const loadingMessage = isEditing ? 'Updating card...' : 'Adding card...';
        const successMessage = isEditing
            ? 'Card updated successfully!'
            : 'Payment method added successfully!';
        const errorMessage = isEditing
            ? 'Failed to update card. Please try again.'
            : 'Failed to add card. Please try again.';

        toast.promise(
            cardPromise.then((resultAction) => {
                if (resultAction.status === 0) {
                    throw new Error(
                        resultAction.message ||
                            (isEditing ? 'Failed to update card.' : 'Failed to add card.'),
                    );
                }

                // Refresh user profile to get updated card list
                dispatch(fetchUserPayments());

                // Reset form and close modal
                resetForm();
                setOpen(false);

                // Call success callback
                if (onSuccess) {
                    onSuccess();
                }

                return resultAction;
            }),
            {
                loading: loadingMessage,
                success: (res) => res.message || successMessage,
                error: (err) => err.message || errorMessage,
            },
            {
                success: {
                    duration: 3000,
                },
                error: {
                    duration: 4000,
                },
            },
        );
    };

    const isFormValid =
        Object.keys(errors).length === 0 &&
        cardDetails.cardNumber &&
        cardDetails.name &&
        cardDetails.expiryDate &&
        cardDetails.cvv;

    // Check if we're in edit mode
    const isEditMode = cardData && cardData.payment_id;

    return (
        <Dialog open={open} onClose={handleClose} className='relative z-50'>
            <DialogBackdrop className='fixed inset-0 bg-black/50' />
            <div className='fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto'>
                <DialogPanel className='bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto'>
                    <div className='flex items-center justify-between mb-6 bg-red-gradient p-4'>
                        <DialogTitle className='text-lg letter-spacing-1 font-normal text-white flex items-center space-x-3'>
                            <CreditCard className='h-6 w-6' />
                            <span>{title}</span>
                        </DialogTitle>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className='text-white transition-colors disabled:opacity-50'
                        >
                            <X size={24} />
                        </button>
                    </div>

                    <div className='space-y-4 px-4'>
                        <div>
                            <label className='block text-sm letter-spacing-07 font-medium text-gray-700 mb-2'>
                                Credit Card Number
                            </label>
                            <input
                                type='text'
                                name='cardNumber'
                                value={cardDetails?.cardNumber}
                                onChange={handleChange}
                                onKeyPress={(e) => {
                                    // Only allow numeric input
                                    if (!/[0-9]/.test(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                                placeholder='1234 5678 9012 3456'
                                disabled={loading}
                                inputMode='numeric'
                                pattern='[0-9\s]*'
                                className={`w-full px-3 py-3 border-0 border-b-2 bg-transparent focus:outline-none focus:border-b-red-300 transition-colors disabled:opacity-50 ${
                                    errors.cardNumber ? 'border-b-red-500' : 'border-b-gray-300'
                                }`}
                                required
                            />
                            {errors.cardNumber && (
                                <p className='text-red-500 letter-spacing-07 text-xs mt-1'>
                                    {errors.cardNumber}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className='block text-sm letter-spacing-07 font-medium text-gray-700 mb-2'>
                                Cardholder Name
                            </label>
                            <input
                                type='text'
                                name='name'
                                placeholder='JOHN DOE'
                                value={cardDetails.name}
                                onChange={handleChange}
                                disabled={loading}
                                className={`w-full px-3 py-3 border-0 border-b-2 bg-transparent focus:outline-none focus:border-b-red-300 transition-colors disabled:opacity-50 ${
                                    errors.name ? 'border-b-red-500' : 'border-b-gray-300'
                                }`}
                                required
                            />
                            {errors.name && (
                                <p className='text-red-500 letter-spacing-07 text-xs mt-1'>
                                    {errors.name}
                                </p>
                            )}
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                            <div>
                                <label className='block letter-spacing-07 text-sm font-medium text-gray-700 mb-2'>
                                    Expiry Date
                                </label>
                                <input
                                    type='text'
                                    name='expiryDate'
                                    placeholder='MM/YY'
                                    value={cardDetails.expiryDate}
                                    onChange={handleChange}
                                    onKeyPress={(e) => {
                                        // Only allow numeric input
                                        if (!/[0-9]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    inputMode='numeric'
                                    disabled={loading}
                                    className={`w-full px-3 py-3 border-0 border-b-2 bg-transparent focus:outline-none focus:border-b-red-300 transition-colors disabled:opacity-50 ${
                                        errors.expiryDate ? 'border-b-red-500' : 'border-b-gray-300'
                                    }`}
                                    required
                                />
                                {errors.expiryDate && (
                                    <p className='text-red-500 letter-spacing-07 text-xs mt-1'>
                                        {errors.expiryDate}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className='block letter-spacing-07 text-sm font-medium text-gray-700 mb-2'>
                                    CVV{' '}
                                </label>
                                <input
                                    type='password'
                                    placeholder='***'
                                    name='cvv'
                                    value={cardDetails.cvv}
                                    onChange={handleChange}
                                    onKeyPress={(e) => {
                                        // Only allow numeric input
                                        if (!/[0-9]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    inputMode='numeric'
                                    disabled={loading}
                                    className={`w-full px-3 py-3 border-0 border-b-2 bg-transparent focus:outline-none focus:border-b-red-300 transition-colors disabled:opacity-50 ${
                                        errors.cvv ? 'border-b-red-500' : 'border-b-gray-300'
                                    }`}
                                    required
                                />
                                {errors.cvv && (
                                    <p className='text-red-500 letter-spacing-07 text-xs mt-1'>
                                        {errors.cvv}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className='flex items-center gap-2 py-2'>
                            <input
                                type='checkbox'
                                name='sameAsFeaturePuchases'
                                id='sameAsFeaturePuchases'
                                checked={cardDetails.sameAsFeaturePuchases}
                                onChange={handleChange}
                                disabled={loading}
                                className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50'
                            />
                            <label
                                htmlFor='sameAsFeaturePuchases'
                                className='text-sm text-gray-700 cursor-pointer'
                            >
                                Save card information for future purchases
                            </label>
                        </div>

                        <div className='flex justify-center py-4'>
                            <button
                                type='button'
                                onClick={handleSubmit}
                                disabled={loading || !isFormValid}
                                className={`w-3/5 py-3 rounded-lg font-medium transition-colors ${
                                    loading || !isFormValid
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'main-btn hover:opacity-90'
                                }`}
                            >
                                {loading ? <ButtonLoader /> : btnname}
                            </button>
                        </div>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
};

export default AddCardDetails;
